/**
 * Subscription domain types (frontend)
 *
 * Mirror del backend para consumir la API. Las fechas vienen como ISO
 * string desde el backend, los hooks las parsean a Date al usar.
 */

export type SubscriptionStatus =
  | 'TRIAL'
  | 'ACTIVE'
  | 'GRACE_PERIOD'
  | 'SUSPENDED'
  | 'CANCELLED'

export interface Plan {
  id: string
  code: string
  name: string
  description: string | null
  maxUsers: number | null
  maxStudiesPerMonth: number | null
  maxStorageGb: number | null
  monthlyPriceCop: number
  isPilot: boolean
  trialDays: number
  gracePeriodDays: number
  features: Record<string, unknown>
  isActive: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface InstitutionSubscription {
  id: string
  institutionId: string
  planId: string
  status: SubscriptionStatus
  startsAt: string
  trialEndsAt: string | null
  expiresAt: string | null
  gracePeriodEndsAt: string | null
  suspendedAt: string | null
  suspendedReason: string | null
  cancelledAt: string | null
  cancelledReason: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface SubscriptionWithPlan extends InstitutionSubscription {
  plan: Plan
}

export interface InstitutionPayment {
  id: string
  institutionId: string
  subscriptionId: string
  amountCop: number
  paidAt: string
  periodStart: string
  periodEnd: string
  paymentMethod: string | null
  reference: string | null
  notes: string | null
  recordedBy: string | null
  createdAt: string
}

export interface AssignSubscriptionInput {
  planCode: string
  trialDaysOverride?: number
  notes?: string
}

export interface RecordPaymentInput {
  subscriptionId: string
  amountCop: number
  paidAt: string
  periodStart: string
  periodEnd: string
  paymentMethod?: string
  reference?: string
  notes?: string
}

export interface UpdatePlanPricingInput {
  monthlyPriceCop: number
}

export interface ExtendTrialInput {
  extraDays: number
  reason: string
}

export interface SuspendSubscriptionInput {
  reason: string
}

export interface PaymentListResponse {
  payments: InstitutionPayment[]
  total: number
}

/**
 * Etiqueta legible para mostrar en UI.
 */
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  TRIAL: 'Periodo de prueba',
  ACTIVE: 'Activa',
  GRACE_PERIOD: 'En cortesia',
  SUSPENDED: 'Suspendida',
  CANCELLED: 'Cancelada',
}

/**
 * Color sugerido por estado para badges.
 * Compatible con tailwind classes existentes (bg-*-100 / text-*-800).
 */
export const SUBSCRIPTION_STATUS_COLORS: Record<SubscriptionStatus, string> = {
  TRIAL: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  GRACE_PERIOD: 'bg-yellow-100 text-yellow-800',
  SUSPENDED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-800',
}
